"use client";

import { CodeBlock } from "./code-block";
import { SecretNote } from "./secret-note";
import { mcpConfig } from "../../lib/mcp-config";
import { ClaudeCodeIcon } from "../ClientIcon";

// // // //

/**
 * ClaudeCodeInstructions
 * Setup for the Claude Code CLI: a single `claude mcp add` command with the
 * API key in the Authorization header. Reused in placeholder mode (static
 * guide, copy off) and live mode (wizard, copy on with the real key).
 */
export function ClaudeCodeInstructions(props: {
    apiKey: string;
    enableCopy?: boolean;
}) {
    const { apiKey, enableCopy = true } = props;

    const command = [
        `claude mcp add --transport http ${mcpConfig.serverSlug} ${mcpConfig.serverUrl} \\`,
        `  --header "Authorization: Bearer ${apiKey}"`,
    ].join("\n");

    return (
        <div className="flex flex-col gap-4">
            <ol className="flex flex-col gap-1.5 text-sm leading-relaxed text-muted-foreground">
                <Numbered n={1}>
                    Make sure the{" "}
                    <span className="text-foreground">Claude Code</span> CLI is
                    installed and you&apos;re signed in.
                </Numbered>
                <Numbered n={2}>
                    Run the command below in your terminal to add the{" "}
                    {mcpConfig.serverName} MCP server.
                </Numbered>
            </ol>

            <div>
                <div className="mb-2 flex items-center gap-2">
                    <ClaudeCodeIcon className="size-4 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">
                        Claude Code
                    </span>
                </div>
                {/* Force a dark terminal (dark bg + light text) regardless of theme. */}
                <CodeBlock
                    code={command}
                    enableCopy={enableCopy}
                    className="dark"
                />
            </div>

            {/* Only warn about the real key in the live wizard, not the
                placeholder preview. */}
            {enableCopy ? <SecretNote /> : null}
        </div>
    );
}

function Numbered({ n, children }: { n: number; children: React.ReactNode }) {
    return (
        <li className="flex gap-2.5">
            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-secondary-foreground">
                {n}
            </span>
            <span>{children}</span>
        </li>
    );
}
